import * as React from 'react'
import { Button, Card } from 'react-bootstrap'
import { useNavigate, useParams } from 'react-router-dom'
import { PositionNumber, PositionNumberType } from 'api/common/constants'
import { BinaryMapTree } from 'api/common/types'
import { referrerGraphBinaryMapAPI } from 'api/referrerGraph'
import Footer from 'components/Footer'
import Header from 'components/Header'
import InputSelectField from 'components/InputSelectField'
import loginData from 'utilities/loginData'
import { ReferrerGraph, referrerGraphs, KeyValue, positions } from 'utilities/resources/options'
import { autoCast } from 'utilities/typeUtility'

type Params = {
  positionNumber: string
  positionId: string
}

const findPositionStyle = (binaryMapTree: BinaryMapTree | null | undefined) => {
  let style = ''
  if (binaryMapTree) {
    if (binaryMapTree.unusedFlag) {
      style = 'unused-position'
    } else if (binaryMapTree.referFlag) {
      style = 'refer-position'
    } else if (binaryMapTree.changedAssignmentFlag) {
      style = 'changed-position'
    } else if (binaryMapTree.positionNumber === PositionNumber.MainPosition) {
      style = 'main-position'
    } else {
      style = 'sub-position'
    }
  } else {
    style = 'invisible'
  }
  return `position-graph ${style}`
}
const findLineLeftStyle = (binaryMapTree: BinaryMapTree | null | undefined) => {
  let style = binaryMapTree ? '' : ' invisible'
  return `binary-map-line left${style}`
}
const findLineRightStyle = (binaryMapTree: BinaryMapTree | null | undefined) => {
  let style = binaryMapTree ? '' : ' invisible'
  return `binary-map-line right${style}`
}

/**
 * 紹介者管理バイナリーマップ
 */
const SceneComponent: React.FC = () => {
  const navigate = useNavigate()
  const params = useParams<Params>()
  const positionNumber = autoCast<PositionNumberType>(params?.positionNumber ?? PositionNumber.MainPosition)
  const targetPositionId = Number(params?.positionId ?? 0)
  const [basePosition, setBasePosition] = React.useState<BinaryMapTree>()
  const [selectablePositions, setSelectablePositions] = React.useState<KeyValue[]>([])
  const [binaryPositionWidth, setBinaryPositionWidth] = React.useState<number>(70)
  const changeGraph = (value: string) => {
    switch (value) {
      case ReferrerGraph.UniLevelList: {
        navigate(`/referrerManagementUniLevelList/${positionNumber}/0/1`)
        break
      }
      case ReferrerGraph.PersonalGroupList: {
        navigate(`/referrerManagementPersonalGroupList/${positionNumber}/-/1`)
        break
      }
    }
  }
  const digPosition = (binaryMapTree: BinaryMapTree | null | undefined) => {
    if (binaryMapTree?.left || binaryMapTree?.right) {
      navigate(`/referrerManagementBinaryMap/${positionNumber}/${binaryMapTree.positionId}`)
    }
  }
  React.useEffect(() => {
    if (!loginData.isLoggedIn()) {
      navigate('/')
    } else {
      referrerGraphBinaryMapAPI(positionNumber, targetPositionId, (response) => {
        if (response) {
          setSelectablePositions(positions.filter(({ key }) => response.selectablePositions.includes(autoCast(key))))
        }
        setBasePosition(response?.binaryMapTree)
      })
      const rect = document.getElementById('binary-map-container')?.getBoundingClientRect()
      if (rect) {
        setBinaryPositionWidth(rect.width / 4)
      }
    }
  }, [navigate, positionNumber, targetPositionId])
  return (
    <>
      <Header title="紹介者管理" />
      <main>
        <section className="mx-4 my-5">
          <InputSelectField name="view-type" label="種別選択" items={referrerGraphs} bold setValue={changeGraph} value={ReferrerGraph.BinaryMap} />
          <InputSelectField name="position" label="ポジション選択" items={selectablePositions} bold setValue={(value) => navigate(`/referrerManagementBinaryMap/${value}/0`)} />
        </section>
        <section className="mx-4 my-5">
          <Card body className="border-0 mt-3">
            <div className="fw-bold">コネクターID</div>
            <hr className="my-2" />
            <div>{basePosition?.positionId ?? '-'}</div>
            <div className="d-flex mt-4">
              <div className="d-flex align-items-center">
                <div className="position-graph-sample main-position"></div>
                <div className="ms-2 font-13">メインポジション</div>
              </div>
              <div className="d-flex align-items-center ms-3">
                <div className="position-graph-sample sub-position"></div>
                <div className="ms-2 font-13">サブポジション</div>
              </div>
            </div>
            <div className="d-flex mt-1">
              <div className="d-flex align-items-center">
                <div className="position-graph-sample refer-position"></div>
                <div className="ms-2 font-13">紹介ポジション</div>
              </div>
              <div className="d-flex align-items-center ms-3">
                <div className="position-graph-sample unused-position"></div>
                <div className="ms-2 font-13">未契約・解約</div>
              </div>
              {loginData.getRegistrationStatus() && (
                <div className="d-flex align-items-center ms-3">
                  <div className="position-graph-sample changed-position"></div>
                  <div className="ms-2 font-13">名義変更</div>
                </div>
              )}
            </div>
            <div className="mt-5" id="binary-map-container">
              <div className="binary-map-row">
                <div className={findPositionStyle(basePosition)}>{basePosition?.positionId}</div>
              </div>
              <div className="binary-map-row">
                <div className={findLineLeftStyle(basePosition?.left)} style={{ width: binaryPositionWidth }}></div>
                <div className={findLineRightStyle(basePosition?.right)} style={{ width: binaryPositionWidth }}></div>
              </div>
              <div className="binary-map-row">
                <div className="binary-map-col" style={{ width: binaryPositionWidth * 2 }}>
                  <button onClick={() => digPosition(basePosition?.left)} disabled={!basePosition?.left}>
                    <div className={findPositionStyle(basePosition?.left)}>{basePosition?.left?.positionId}</div>
                  </button>
                </div>
                <div className="binary-map-col" style={{ width: binaryPositionWidth * 2 }}>
                  <button onClick={() => digPosition(basePosition?.right)} disabled={!basePosition?.right}>
                    <div className={findPositionStyle(basePosition?.right)}>{basePosition?.right?.positionId}</div>
                  </button>
                </div>
              </div>
              <div className="binary-map-row">
                <div className={findLineLeftStyle(basePosition?.left?.left)} style={{ width: binaryPositionWidth / 2 }}></div>
                <div className={findLineRightStyle(basePosition?.left?.right)} style={{ width: binaryPositionWidth / 2 }}></div>
                <div style={{ width: binaryPositionWidth }}></div>
                <div className={findLineLeftStyle(basePosition?.right?.left)} style={{ width: binaryPositionWidth / 2 }}></div>
                <div className={findLineRightStyle(basePosition?.right?.right)} style={{ width: binaryPositionWidth / 2 }}></div>
              </div>
              <div className="binary-map-row">
                <div className="binary-map-col" style={{ width: binaryPositionWidth }}>
                  <button onClick={() => digPosition(basePosition?.left?.left)} disabled={!basePosition?.left?.left}>
                    <div className={findPositionStyle(basePosition?.left?.left)}>{basePosition?.left?.left?.positionId}</div>
                  </button>
                </div>
                <div className="binary-map-col" style={{ width: binaryPositionWidth }}>
                  <button onClick={() => digPosition(basePosition?.left?.right)} disabled={!basePosition?.left?.right}>
                    <div className={findPositionStyle(basePosition?.left?.right)}>{basePosition?.left?.right?.positionId}</div>
                  </button>
                </div>
                <div className="binary-map-col" style={{ width: binaryPositionWidth }}>
                  <button onClick={() => digPosition(basePosition?.right?.left)} disabled={!basePosition?.right?.left}>
                    <div className={findPositionStyle(basePosition?.right?.left)}>{basePosition?.right?.left?.positionId}</div>
                  </button>
                </div>
                <div className="binary-map-col" style={{ width: binaryPositionWidth }}>
                  <button onClick={() => digPosition(basePosition?.right?.right)} disabled={!basePosition?.right?.right}>
                    <div className={findPositionStyle(basePosition?.right?.right)}>{basePosition?.right?.right?.positionId}</div>
                  </button>
                </div>
              </div>
            </div>
            {targetPositionId > 0 && (
              <>
                <div className="mt-5 text-center">
                  <Button variant="link" onClick={() => navigate(-1)}>
                    一つ前の表示に戻る
                  </Button>
                </div>
                <div className="mt-3 text-center">
                  <Button variant="link" onClick={() => navigate(`/referrerManagementBinaryMap/${positionNumber}/0`)}>
                    最初に戻る
                  </Button>
                </div>
              </>
            )}
          </Card>
        </section>
      </main>
      <Footer className="mt-5" />
    </>
  )
}

export default SceneComponent
